import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
} from '@mui/material';
import { Add as PlusIcon, Delete, Edit } from '@mui/icons-material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  deleteOrganizationNote,
  getAllOrganizationNotes,
  updateOrganizationNote,
  addOrganizationNote,
} from '@/repository/OrganizationDAO';
import { useSafeguardAdminSignalRContext } from '@/context_providers/signal_r_context/SafeguardAdminSignalRListener';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import AddNoteModal from './modals/AddNoteModal';
import EditNoteModal from './modals/EditNoteModal';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import Loading from '@/components/loading/Loading';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { ToastType } from '@/context_providers/toast_context/ToastContext';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import COLORS from '@/resources/style/Themes';
import PageHeader from '@/components/utils/PageHeader';
import {
  OrgNoteCreate,
  OrgNoteOnAzure,
  OrgNoteUpdate,
} from '@/../generated-client/models';

const Notes = () => {
  const [responseData, setResponseData] = useState<OrgNoteOnAzure[]>();
  const { organization: contextOrg } = useSafeguardAdminSignalRContext();
  const { bearerToken } = useUserContext();
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState<OrgNoteOnAzure>();
  const [messageToHighlight, setMessageToHighlight] = useState<string | null>(
    null,
  );
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [noteToRemove, setNoteToRemove] = useState<OrgNoteOnAzure>();
  const { showToast } = useToast();

  useEffect(() => {
    const fetchAllOrganizationNotes = async () => {
      try {
        const notes = await getAllOrganizationNotes(contextOrg.org_code);
        setResponseData(notes);
      } catch (e) {
        console.warn('Error fetching notes');
      }
    };
    fetchAllOrganizationNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearerToken]);

  useEffect(() => {
    if (messageToHighlight != null) {
      setTimeout(() => {
        setMessageToHighlight(null); // clear the id after a period of time (e.g. 2 seconds)
      }, 2000);
    }
  }, [messageToHighlight]);

  const updateNote = async (editedNote: OrgNoteOnAzure) => {
    try {
      const requestBody: OrgNoteUpdate = {
        note: editedNote.note,
        note_name: editedNote.note_name,
        org_code: editedNote.org_code,
        org_note_azure_id: editedNote.id,
      };

      await updateOrganizationNote(requestBody);
      const currentUnixTimestamp = Math.floor(Date.now() / 1000);
      const updatedNote: OrgNoteOnAzure = {
        note: editedNote.note,
        note_name: editedNote.note_name,
        org_code: editedNote.org_code,
        _ts: currentUnixTimestamp,
        id: editedNote.id,
        _etag: editedNote._etag,
        object_type: editedNote.object_type,
        unique_key: editedNote.unique_key,
      };
      if (!responseData) {
        return;
      }
      const index = responseData?.findIndex(
        (note) => note.id === updatedNote.id,
      );
      const updatedData = [...responseData];
      updatedData[index] = updatedNote;
      setResponseData(updatedData);
      showToast(
        `Successfully updated ${updatedNote.note_name}.`,
        ToastType.SUCCESS,
      );
      setMessageToHighlight(updatedNote.id);
    } catch (e) {
      showToast(`Failed to update ${editedNote.note_name}`, ToastType.DANGER);
    }
  };

  const addNote = async (newNote: OrgNoteCreate) => {
    try {
      const createdNote = await addOrganizationNote(newNote);

      showToast(
        `Successfully added ${newNote.note_name} configuration.`,
        ToastType.SUCCESS,
      );
      setMessageToHighlight(createdNote.id);

      setResponseData((prevData) => {
        if (prevData) {
          return [...prevData, createdNote];
        } else {
          return [createdNote];
        }
      });
    } catch (error) {
      showToast(
        `Failed to add ${newNote.note_name}. Please try again.`,
        ToastType.DANGER,
      );
    }
  };

  const deleteNote = async () => {
    setShowConfirmModal(false);
    if (noteToRemove) {
      try {
        await deleteOrganizationNote(noteToRemove);
        const newResponseData = responseData?.filter(
          (message) => message.id !== noteToRemove?.id,
        );
        setResponseData(newResponseData);
        showToast(
          `Successfully removed message ${noteToRemove?.note_name}.`,
          ToastType.SUCCESS,
        );
      } catch (e) {
        showToast(
          `Failed to remove message ${noteToRemove?.note_name}. Please try again.`,
          ToastType.DANGER,
        );
      } finally {
        setNoteToRemove(undefined);
      }
    } else {
      console.error("Tried to remove note that doesn't exist");
      showToast(
        "That note doesn't exist. Try refreshing the page",
        ToastType.DANGER,
      );
    }
  };

  const renderData = () => {
    if (responseData) {
      return (
        <>
          <PageHeader
            disabled={false}
            action={() => {
              setShowAddNoteModal(true);
            }}
            icon={<PlusIcon style={{ color: COLORS.WHITE }} />}
            header={`${contextOrg.company_name} Notes`}
            label={'Add New'}
          />
          <TableContainer sx={{ boxShadow: 0 }} component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Note Name</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Edit</TableCell>
                  <TableCell>Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {responseData.map((item, index) => {
                  const unixTimestamp = item._ts ?? Date.now();
                  const date = new Date(unixTimestamp * 1000);

                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const year = date.getFullYear();
                  const readableDate = `${month}/${day}/${year}`;

                  return (
                    <TableRow
                      key={index}
                      className={
                        item.id === messageToHighlight ? 'flash-green' : ''
                      }
                    >
                      <TableCell>{item.note_name}</TableCell>
                      <TableCell>{item.note}</TableCell>
                      <TableCell>{readableDate}</TableCell>
                      <TableCell>
                        <IconButton
                          style={{
                            borderRadius: '10px',
                            width: '40px',
                            height: '40px',
                            border: 'none',
                            backgroundColor: COLORS.LIGHT_GREEN,
                          }}
                          onClick={() => {
                            setNoteToEdit(item);
                            setShowEditNoteModal(true);
                          }}
                        >
                          <Edit style={{ color: COLORS.DARK_GREEN }} />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          style={{
                            borderRadius: '10px',
                            width: '40px',
                            height: '40px',
                            border: 'none',
                            backgroundColor: COLORS.PINK,
                          }}
                          onClick={() => {
                            setConfirmTitle(`Delete ${item.note_name}`);
                            setConfirmMessage(
                              `Are you sure you would like to delete message ${item.note_name}?`,
                            );
                            setNoteToRemove(item);
                            setShowConfirmModal(true);
                          }}
                        >
                          <Delete style={{ color: COLORS.DARK_RED }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      );
    } else {
      return <Typography>No data available</Typography>;
    }
  };

  return (
    <>
      {responseData ? renderData() : <LoadingSpinner />}

      <AddNoteModal
        show={showAddNoteModal}
        onHide={() => setShowAddNoteModal(false)}
        organization={contextOrg}
        onCreate={addNote}
      />

      <EditNoteModal
        show={showEditNoteModal}
        onHide={() => setShowEditNoteModal(false)}
        note={noteToEdit}
        onSave={updateNote}
      />

      <ConfirmModal
        show={showConfirmModal}
        confirm={deleteNote}
        onHide={() => {
          setNoteToRemove(undefined);
          setShowConfirmModal(false);
        }}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />
    </>
  );
};

export default withAuthenticationRequired(Notes, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
